import React, { useEffect, useMemo, useRef } from 'react'

import moment from 'moment'
import qs from 'qs'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { POSTHOG_TOOLBAR_STORAGE_KEY } from 'constants/analytics'
import { PAGE_URL } from 'constants/page_url'
import * as FilterActions from 'modules/filter/actions'
import * as FilterSelectors from 'modules/filter/selectors'
import { ITimePeriod } from 'modules/filter/types'
import { calculateTimePeriod, getFilterQueryString } from 'modules/filter/utils'
import { trackPageView } from 'utils/tracking'

interface IStateProps {
  timePeriod: ITimePeriod
}

interface IDispatchProps {
  actions: {
    updateReportTimePeriod: (timePeriod: ITimePeriod) => void
  }
}

type Props = IStateProps & IDispatchProps

// Some reports use different or no query params
const NO_QUERY_PARAMS_PATHS = [
  PAGE_URL.CONVO_ANALYSIS,
  PAGE_URL.CONVO_DETAIL,
  PAGE_URL.REAL_TIME,
  'playbooks/chat/site-coverage',
  PAGE_URL.TEAM_AVAILABILITY,
  PAGE_URL.REVENUE_OPPORTUNITIES,
  PAGE_URL.REVENUE_OVERVIEW,
  PAGE_URL.AI_PERFORMANCE_OVERVIEW,
  PAGE_URL.AI_CONVERSATION_INSIGHTS,
]

/**
 * Because in reports-web we often swap the URL before our analytics vendor loads we need to properly
 * stash the data from the URL in the instance that someone is launching a toolbar to generate actions from.
 */
const stashAnalyticsToolbarData = () => {
  const toolbarJSON = new URLSearchParams(
    window.location.hash.substring(1)
  ).get('__posthog')
  if (toolbarJSON) {
    window.localStorage &&
      window.localStorage.setItem(POSTHOG_TOOLBAR_STORAGE_KEY, toolbarJSON)
  }
}

const withFilterQueryParams = (Component: React.ComponentType<any>) =>
  function WithFilterQueryParams(props: Props) {
    const { actions, timePeriod } = props

    const { pathname, search } = useLocation()
    const history = useHistory()

    const initialized = useRef<boolean>(false)
    const pathIsSiteTraffic = useMemo(
      () => history.location.pathname.includes('site-coverage'),
      [history]
    )

    useEffect(() => trackPageView('Reporting Route Page View'), [pathname])

    const initializeAndUpdateTimePeriodIfQueryParamsAvailable = () => {
      // NOTE: this use effect updates the time period when it detects queryParams

      if (initialized.current || pathIsSiteTraffic) {
        return
      }

      const queryParams = qs.parse(search, { ignoreQueryPrefix: true })

      if (queryParams.startDate || queryParams.endDate) {
        actions.updateReportTimePeriod(
          calculateTimePeriod(
            moment(queryParams.startDate),
            moment(queryParams.endDate)
          )
        )
      }

      initialized.current = true
    }
    useEffect(initializeAndUpdateTimePeriodIfQueryParamsAvailable, [
      actions,
      search,
      initialized,
      history.location.pathname,
      pathIsSiteTraffic,
      pathname,
    ])

    const updateQueryParamWithDefaultTimePeriod = () => {
      if (!initialized.current || pathIsSiteTraffic) {
        return
      }

      const queryString = getFilterQueryString(timePeriod)

      if (!NO_QUERY_PARAMS_PATHS.some((path) => pathname.includes(path))) {
        stashAnalyticsToolbarData()
        history.replace(queryString)
      }
    }

    useEffect(updateQueryParamWithDefaultTimePeriod, [
      history,
      initialized,
      pathIsSiteTraffic,
      search,
      pathname,
      timePeriod,
      actions,
    ])

    return <Component {...props} />
  }

const mapStateToProps = (state): IStateProps => ({
  timePeriod: FilterSelectors.timePeriod(state),
})

const mapDispatchToProps = (dispatch): IDispatchProps => ({
  actions: bindActionCreators(
    {
      updateReportTimePeriod: FilterActions.updateReportTimePeriod,
    },
    dispatch
  ),
})

export default (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withFilterQueryParams(Component))
